import React, { useMemo } from 'react'
import {
  Header,
  Icon,
  Section,
  Table,
  TableBody,
  TableBodyCell,
  TableHead,
  TableHeadCell,
  TableRow,
  Text,
  GridRow,
  ButtonLink,
  Button,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import { getDisplayNameFromApiCustomer } from '@vend/react-utilities'
import { DATE_TIME_LONG, formatDateTime } from '@vend/utilities'
import { Currency } from '@vend/business-components'
import { Sale } from '../../models/sale'
import { WEB_REGISTER } from '../web-register.constant'
import { useSaleDisplayStatus } from '../util/useSaleDisplayStatus'
import { User } from '../../models/sale.constants'

interface SaleTableRowProps {
  sale: Sale
  timeZone: string
}

const SaleTableRow: React.FC<SaleTableRowProps> = ({ sale, timeZone }) => {
  const user = sale.user as User
  const username = user?.display_name || user?.username || '-'
  const customer = sale.customer
    ? getDisplayNameFromApiCustomer(sale.customer)
    : '-'
  const saleStatus = sale.displayStatus || sale.status
  const getSaleDisplayStatus = useSaleDisplayStatus()
  const dateFormatOptions: Intl.DateTimeFormatOptions = timeZone
    ? { ...DATE_TIME_LONG, timeZone }
    : DATE_TIME_LONG

  return (
    <TableRow>
      <TableBodyCell>
        <Text tag="div">{sale.getFormattedReceiptNumberDetails() || '-'}</Text>
        {sale.saleDate && (
          <Text type="supplementary">
            {formatDateTime(sale.saleDate, dateFormatOptions)}
          </Text>
        )}
      </TableBodyCell>
      <TableBodyCell>{username}</TableBodyCell>
      <TableBodyCell>{customer}</TableBodyCell>
      <TableBodyCell>
        {saleStatus ? getSaleDisplayStatus(saleStatus) : '-'}
      </TableBodyCell>
      <TableBodyCell>{sale.note || '-'}</TableBodyCell>
      <TableBodyCell>
        <Currency value={sale.finals.inclusivePrice} />
      </TableBodyCell>
    </TableRow>
  )
}

interface Props {
  sales: Sale[]
  syncInProgress: boolean
  fileUrl: string
  timeZone: string
}

export const ErrorSales: React.FC<Props> = ({
  sales,
  syncInProgress,
  fileUrl,
  timeZone,
}) => {
  const t = useT()

  const downloadButtonText = useMemo(() => {
    if (syncInProgress) {
      return t('Syncing sales...', {
        _context: 'Button text when action is in progress',
      })
    } else {
      return t('Download sales info', { _context: 'Button text' })
    }
  }, [syncInProgress, t])

  return (
    <Section>
      <Header type="sub-heading" level="4" className="">
        <Icon icon="warning" className="vd-text--negative vd-mr1"></Icon>
        {t(
          '{salesCount, plural,' +
            'one {Unable to sync {salesCount} sale to the server}' +
            'other {Unable to sync {salesCount} sales to the server}}',
          {
            salesCount: sales.length,
          }
        )}
      </Header>
      <Text tag="p" className="vd-mb5">
        {t(
          '{salesCount, plural,' +
            'one {There was a problem syncing {salesCount} of your sales to ' +
            'the server. This sale has been temporarily saved in your ' +
            'browser. It’s important that you avoid clearing your browser ' +
            'cache, otherwise the sale will be deleted permanently.}' +
            'other {There was a problem syncing {salesCount} of your sales ' +
            'to the server. These sales have been temporarily saved in your ' +
            'browser. It’s important that you avoid clearing your browser ' +
            'cache, otherwise the sales will be deleted permanently.}}',
          {
            salesCount: sales.length,
          }
        )}
      </Text>
      <Text tag="p" className="vd-mb5">
        {t(
          '{salesCount, plural,' +
            'one {Download the sale information and send it to Lightspeed’s ' +
            'Support team. This will help to have the issue resolved as ' +
            'fast as possible.} ' +
            'other {Download the sales information and send it to Lightspeed’s ' +
            'Support team. This will help to have the issue resolved as ' +
            'fast as possible.}}',
          {
            salesCount: sales.length,
          }
        )}
      </Text>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeadCell>
              <T _str="Receipt" _context="TableHeadCell" />
            </TableHeadCell>
            <TableHeadCell>
              <T _str="Sold by" _context="TableHeadCell" />
            </TableHeadCell>
            <TableHeadCell>
              <T _str="Customer" _context="TableHeadCell" />
            </TableHeadCell>
            <TableHeadCell>
              <T _str="Status" _context="TableHeadCell" />
            </TableHeadCell>
            <TableHeadCell>
              <T _str="Note" _context="TableHeadCell" />
            </TableHeadCell>
            <TableHeadCell>
              <T _str="Sale total" _context="TableHeadCell" />
            </TableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sales.map(sale => (
            <SaleTableRow key={sale.localId} sale={sale} timeZone={timeZone} />
          ))}
        </TableBody>
      </Table>
      <GridRow align="right" className="vd-mt5">
        {syncInProgress ? (
          <Button disabled>{downloadButtonText}</Button>
        ) : (
          <ButtonLink
            href={fileUrl}
            variant="go"
            download="errored-sales.json"
            target="_blank"
          >
            {downloadButtonText}
          </ButtonLink>
        )}
        <ButtonLink
          variant="no"
          className="vd-ml1"
          href={WEB_REGISTER.CONTACT_SUPPORT_URL}
          target="_blank"
        >
          {t('Contact support', { _context: 'Link text' })}
        </ButtonLink>
      </GridRow>
    </Section>
  )
}
